// rounds query-key factories (TanStack Query)
// Hierarchical: all → lists() → list(filters) → details() → detail(id)
// The ONLY thing another module may import from here.
// Direct path, not the `_shared` barrel — the barrel re-exports this file, so going through
// it would make keys.ts import itself.
import type { RoundListFilters } from "./types";

/**
 * Intake rounds. The hierarchy IS the invalidation API: `all` clears the whole entity,
 * `lists()` every filtered list, `detail(id)` one round.
 */
export const roundKeys = {
  all: ["acquire", "rounds"] as const,
  lists: () => [...roundKeys.all, "list"] as const,
  list: (filters: RoundListFilters) => [...roundKeys.lists(), filters] as const,
  details: () => [...roundKeys.all, "detail"] as const,
  detail: (id: string) => [...roundKeys.details(), id] as const,
  /** The document-requirements panel of one round (its own endpoint, its own cache entry). */
  documents: (id: string) => [...roundKeys.detail(id), "documents"] as const,
  /** The application-form panel (`GET /form/`) of one round. */
  form: (id: string) => [...roundKeys.detail(id), "form"] as const,
  /**
   * Publish readiness (`GET /readiness/`) of one round.
   *
   * Nested under `detail(id)` so invalidating the round clears it too — readiness is derived from
   * every other panel, so it is stale the moment any of them is saved.
   */
  readiness: (id: string) => [...roundKeys.detail(id), "readiness"] as const,
  /** Snapshot provenance (`GET /config-snapshot/`) — when the configuration was frozen. */
  snapshot: (id: string) => [...roundKeys.detail(id), "snapshot"] as const,
  /**
   * Configuration/lifecycle audit trail (`GET /configuration-history/`) — "Historique de
   * l'appel". Keyed by filters too (structurally, not by importing the feature's own
   * `RoundHistoryFilters` type — `_shared` doesn't import from a feature): the zone/period
   * pickers re-fetch against the server's real `section`/`from_at`/`to_at` params rather than
   * filtering a client-side copy.
   */
  history: (
    id: string,
    filters: { section?: string; fromAt?: string; toAt?: string } = {}
  ) => [...roundKeys.detail(id), "history", filters] as const,
  /** One round's competency selection (`GET /competency-framework/`). */
  competencies: (id: string) =>
    [...roundKeys.detail(id), "competencies"] as const,
};

/**
 * The competency CATALOGUE the round builder picks from — a separate root from `roundKeys`.
 *
 * Not a branch of any round, because it is not round data: it is the active framework release, read
 * so the builder can offer a choice before a round exists. Nesting it under `roundKeys.all` would
 * mean saving one round's configuration invalidating the catalogue every other round reads.
 *
 * ⚠ `modules/competencies` caches the SAME endpoint under its own root. Two cache entries for one
 * request is the accepted cost of the module boundary — see `services/competencies.ts` for why the
 * read is duplicated rather than shared. They hold different projections, so they are not
 * interchangeable even though the request is the same.
 */
export const templateCatalogueKeys = {
  /**
   * `GET /acquire/template-catalog/` — the message templates step 4 assigns.
   *
   * Its OWN root, not a branch of `roundKeys`, for the same reason the competency catalogue has
   * one: it is not round data. It is the set of templates every round picks from, so a round's
   * configuration save must not invalidate it.
   *
   * Flat — one endpoint, no arguments, nothing to sub-key.
   */
  all: ["acquire", "template-catalogue"] as const,
};

export const competencyCatalogueKeys = {
  all: ["acquire", "competency-catalogue"] as const,
  active: () => [...competencyCatalogueKeys.all, "active"] as const,
};

/**
 * The PUBLIC round catalogue (`GET /acquire/public/intake-rounds/`) — a separate cache root from
 * `roundKeys`, not a branch of it.
 *
 * Same entity, different contract: no credentials, a narrower payload, server-side filtering and
 * ordering. Nesting it under `roundKeys.all` would mean an administrator's mutation invalidating
 * the public cache and vice versa — two caches that must not clear each other, because a staff
 * write does not change what the public may see until the round is published.
 */
export const publicRoundKeys = {
  all: ["acquire", "public-rounds"] as const,
  lists: () => [...publicRoundKeys.all, "list"] as const,
  details: () => [...publicRoundKeys.all, "detail"] as const,
  /**
   * One publicly visible round. Keyed by LOCALE as well as id, because this endpoint localizes
   * server-side — `?locale=` decides the language of the eligibility and document copy, so two
   * locales are two different payloads and must not share a cache entry.
   */
  detail: (id: string, locale: string) =>
    [...publicRoundKeys.details(), id, { locale }] as const,
};

/** The flat Wilaya catalogue (`GET /acquire/wilayas/`). Reference data — rarely changes. */
export const wilayaKeys = {
  all: ["acquire", "wilayas"] as const,
  lists: () => [...wilayaKeys.all, "list"] as const,
};

/** Reusable question bank (`GET /acquire/question-bank/?search=`). */
export const questionBankKeys = {
  all: ["acquire", "question-bank"] as const,
  lists: () => [...questionBankKeys.all, "list"] as const,
  list: (search: string) => [...questionBankKeys.lists(), { search }] as const,
};
